import {
  CANVAS_DOC_ASPECT_RATIO,
  CANVAS_EMAIL_CONTENT_HEIGHT,
  CANVAS_NODE_HEADER_HEIGHT,
  CANVAS_NODE_WIDTH,
  CANVAS_WEB_PAGE_CONTENT_HEIGHT,
} from "@hightouch/lib/llm/canvas/constants";
import type { CanvasContentType } from "src/pages/projects/library/canvas/use-add-content-to-canvas";

type Point = { x: number; y: number };

/** The space between cards that land together, side by side. */
const BATCH_GAP = 24;

/** The most storage takes in one file. */
export const MAX_UPLOAD_BYTES = 1_000_000_000;
/** The most the API takes for a file's name. */
const MAX_NAME_LENGTH = 255;

/** The file's name cut to what the API takes, keeping its extension so the type still reads. */
export function uploadName(name: string) {
  if (name.length <= MAX_NAME_LENGTH) return name;
  const dot = name.lastIndexOf(".");
  const extension = dot > 0 && name.length - dot <= 16 ? name.slice(dot) : "";
  return name.slice(0, MAX_NAME_LENGTH - extension.length) + extension;
}

export type DraggedFiles = { count: number };

/** How many files a drag carries, from what the browser shows before the drop; null when it carries none. */
export function describeDraggedFiles(
  transfer: Pick<DataTransfer, "types" | "items"> | null,
): DraggedFiles | null {
  if (!transfer || !Array.from(transfer.types).includes("Files")) return null;
  const count = Array.from(transfer.items).filter(
    (item) => item.kind === "file",
  ).length;
  return { count };
}

export function dropLabel({ count }: DraggedFiles) {
  if (count === 0) return "Drop to add files";
  return `Drop to add ${count} ${count === 1 ? "file" : "files"}`;
}

/** Top-left corners for a batch of cards laid side by side, centred on `centre`. */
export function layoutBatch(centre: Point, count: number): Point[] {
  const width = count * CANVAS_NODE_WIDTH + (count - 1) * BATCH_GAP;
  const left = centre.x - width / 2;
  return Array.from({ length: count }, (_, index) => ({
    x: Math.round(left + index * (CANVAS_NODE_WIDTH + BATCH_GAP)),
    y: Math.round(centre.y),
  }));
}

export type Rect = Point & { width: number; height: number };

/** How far the search for a clear place steps, and how many steps it takes at most in each direction. */
const CLEAR_STEP = 48;
const CLEAR_REACH = 30;

/** The batch moved as a whole to the nearest place where no card overlaps one already on the canvas. */
export function placeBatchClear(
  preferred: Point[],
  size: { width: number; height: number },
  taken: Rect[],
): Point[] {
  const overlaps = (at: Point) =>
    taken.some(
      (other) =>
        at.x < other.x + other.width + BATCH_GAP &&
        at.x + size.width + BATCH_GAP > other.x &&
        at.y < other.y + other.height + BATCH_GAP &&
        at.y + size.height + BATCH_GAP > other.y,
    );
  const offsets: Point[] = [];
  for (let dx = -CLEAR_REACH; dx <= CLEAR_REACH; dx++)
    for (let dy = -CLEAR_REACH; dy <= CLEAR_REACH; dy++)
      offsets.push({ x: dx * CLEAR_STEP, y: dy * CLEAR_STEP });
  offsets.sort((a, b) => Math.hypot(a.x, a.y) - Math.hypot(b.x, b.y));
  const clear = offsets.find((offset) =>
    preferred.every(
      (at) => !overlaps({ x: at.x + offset.x, y: at.y + offset.y }),
    ),
  );
  if (!clear) return preferred;
  return preferred.map((at) => ({ x: at.x + clear.x, y: at.y + clear.y }));
}

export type NewCardType = "doc" | CanvasContentType;

/** The room a new card's content takes before it renders and is measured, allowing for a long email or web page. */
const NEW_CARD_CONTENT_HEIGHT: Record<NewCardType, number> = {
  // An ad's shape isn't known until it renders; a square is the canvas's own fallback.
  ad: CANVAS_NODE_WIDTH,
  email: CANVAS_EMAIL_CONTENT_HEIGHT,
  html: CANVAS_WEB_PAGE_CONTENT_HEIGHT,
  doc: Math.round(CANVAS_NODE_WIDTH * CANVAS_DOC_ASPECT_RATIO),
};

/** Spots for new cards of these types side by side around `centre`, moved together clear of `taken`, each with the room its card is given. */
export function placeNewCards(
  centre: Point,
  types: readonly NewCardType[],
  taken: Rect[],
): Rect[] {
  // The cards share a row, so each gets the room of the tallest.
  const size = {
    width: CANVAS_NODE_WIDTH,
    height:
      CANVAS_NODE_HEADER_HEIGHT +
      Math.max(...types.map((type) => NEW_CARD_CONTENT_HEIGHT[type])),
  };
  return placeBatchClear(layoutBatch(centre, types.length), size, taken).map(
    (at) => ({ ...at, ...size }),
  );
}

/** True while focus or the event's target is somewhere the keyboard types text. */
export function isTypingTarget(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) return false;
  return Boolean(
    target.closest(
      "input, textarea, select, [contenteditable='true'], [contenteditable='']",
    ),
  );
}
